import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/request';
import type { DocumentResponse } from '@onefootprint/request-types';
import { IdDocImageTypes } from '@onefootprint/types';
import { Button } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import upperFirst from 'lodash/upperFirst';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NavigationHeader from '@/idv/components/layout/components/navigation-header';
import { getLogger, trackAction } from '@/idv/utils';
import DesktopHeader from '../../../components/desktop-header';
import IdDocAnimation from '../../../components/id-doc-animation';
import Loading from '../../../components/loading';
import RetryLimitExceeded from '../../../components/retry-limit-exceeded';
import Success from '../../../components/success';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import useDocName from '../../hooks/use-doc-name';
import useIdDocMachine from '../../hooks/use-id-doc-machine';
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'desktop-processing'
});
const DeskTopProcessing = () => {
  const $ = _c(85);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "document-flow.id-doc.pages.desktop-processing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t0);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, send] = useIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = postHostedUserDocumentsByIdProcessMutation();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const processDocMutation = useMutation(t1);
  const [mode, setMode] = useState("loading");
  const [nextSide, setNextSide] = useState();
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    idDoc: t2,
    image,
    authToken,
    currSide,
    id
  } = state.context;
  const {
    type,
    country
  } = t2;
  const isSelfie = currSide === IdDocImageTypes.selfie;
  let t3;
  if ($[2] !== currSide || $[3] !== type) {
    t3 = {
      docType: type,
      imageType: currSide
    };
    $[2] = currSide;
    $[3] = type;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    getDocName,
    getSideName
  } = useDocName(t3);
  let t4;
  if ($[5] !== getDocName) {
    t4 = upperFirst(getDocName());
    $[5] = getDocName;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const docName = t4;
  let t5;
  if ($[7] !== getSideName) {
    t5 = upperFirst(getSideName());
    $[7] = getSideName;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const sideName = t5;
  let t6;
  if ($[9] !== currSide) {
    t6 = () => {
      trackAction(`document-processing-${currSide}:started`);
    };
    $[9] = currSide;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== country || $[13] !== currSide || $[14] !== docName || $[15] !== id || $[16] !== send || $[17] !== sideName || $[18] !== state.context.currSide || $[19] !== type) {
    t8 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docType: type,
        country,
        side: currSide
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. Side: ${sideName}, doc id: ${id}, type: ${docName}`, undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:retry-limit-exceeded`, rejectionContext);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === state.context.currSide) {
        logWarn(`Document rejected. Side: ${currSide}, codes: ${rejectionCodes}`, undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:rejected`, rejectionContext);
        send({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      setMode("success");
      if (nextSideToCollect) {
        setNextSide(nextSideToCollect as IdDocImageTypes);
      }
      trackAction(`document-processing-${currSide}:completed`, rejectionContext);
    };
    $[12] = country;
    $[13] = currSide;
    $[14] = docName;
    $[15] = id;
    $[16] = send;
    $[17] = sideName;
    $[18] = state.context.currSide;
    $[19] = type;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleProcessDocSuccess = t8;
  let t9;
  if ($[21] !== getErrorMessage || $[22] !== send) {
    t9 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      send({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.networkError,
            errorInfo: getErrorMessage(err_0)
          }]
        }
      });
    };
    $[21] = getErrorMessage;
    $[22] = send;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleError = t9;
  let t10;
  if ($[24] !== getErrorMessage || $[25] !== handleError || $[26] !== id) {
    t10 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing id-doc image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[24] = getErrorMessage;
    $[25] = handleError;
    $[26] = id;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleProcessDocError = t10;
  let t11;
  if ($[28] !== currSide || $[29] !== getErrorCode || $[30] !== getErrorMessage || $[31] !== handleError || $[32] !== id) {
    t11 = err_2 => {
      const errorCode = getErrorCode(err_2);
      const errorMessage = getErrorMessage(err_2);
      if (errorCode === "E109") {
        setNextSide(undefined);
        setMode("success");
        return;
      }
      handleError(err_2);
      logWarn(`Id-doc image submit failed on phone flow. Side: ${currSide}, upload session id: ${id}. Error: ${errorMessage}`, err_2);
    };
    $[28] = currSide;
    $[29] = getErrorCode;
    $[30] = getErrorMessage;
    $[31] = handleError;
    $[32] = id;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleSubmitDocError = t11;
  let t12;
  if ($[34] !== authToken || $[35] !== handleProcessDocError || $[36] !== handleProcessDocSuccess || $[37] !== id || $[38] !== processDocMutation) {
    t12 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t13) {
        const error = t13;
        handleProcessDocError(error);
      }
    };
    $[34] = authToken;
    $[35] = handleProcessDocError;
    $[36] = handleProcessDocSuccess;
    $[37] = id;
    $[38] = processDocMutation;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const handleSubmitDocSuccess = t12;
  let t13;
  if ($[40] !== authToken || $[41] !== country || $[42] !== currSide || $[43] !== handleSubmitDocError || $[44] !== handleSubmitDocSuccess || $[45] !== id || $[46] !== image || $[47] !== submitDocMutation || $[48] !== type) {
    t13 = () => {
      if (!image || !authToken || !type || !country || !currSide || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Desktop web flow - id-doc image could not be processed due to missing requirements. Requirements - image: ${image ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, doc type: ${type ? "OK" : "undefined"}, country: ${country ? "OK" : "undefined"}, current side: ${currSide ? "OK" : "undefined"}, id: ${id ? "OK" : "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind,
        forcedUpload
      } = image;
      logInfo(`Uploading image with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type} in POST request`);
      submitDocMutation.mutate({
        authToken,
        image: imageFile,
        extraCompress: extraCompressed,
        side: currSide,
        id,
        forceUpload: forcedUpload,
        meta: {
          manual: captureKind === "manual",
          isUpload: captureKind === "upload"
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[40] = authToken;
    $[41] = country;
    $[42] = currSide;
    $[43] = handleSubmitDocError;
    $[44] = handleSubmitDocSuccess;
    $[45] = id;
    $[46] = image;
    $[47] = submitDocMutation;
    $[48] = type;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  useEffect(t13, t14);
  let t15;
  if ($[51] !== send) {
    t15 = () => {
      send({
        type: "retryLimitExceeded"
      });
    };
    $[51] = send;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const handleRetryLimitExceeded = t15;
  let t16;
  if ($[53] !== nextSide || $[54] !== send) {
    t16 = () => {
      send({
        type: "processingSucceeded",
        payload: {
          nextSideToCollect: nextSide
        }
      });
    };
    $[53] = nextSide;
    $[54] = send;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const handleNextStep = t16;
  if (isMissingRequirements) {
    let t17;
    if ($[56] !== t) {
      t17 = t(_temp2);
      $[56] = t;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    let t18;
    if ($[58] !== t17) {
      t18 = <p className="text-label-1 text-error text-center fp-text">{t17}</p>;
      $[58] = t17;
      $[59] = t18;
    } else {
      t18 = $[59];
    }
    return t18;
  }
  if (retryLimitExceeded) {
    let t17;
    if ($[60] !== handleRetryLimitExceeded) {
      t17 = <RetryLimitExceeded onRetryLimitExceeded={handleRetryLimitExceeded} />;
      $[60] = handleRetryLimitExceeded;
      $[61] = t17;
    } else {
      t17 = $[61];
    }
    return t17;
  }
  if (!type || !currSide) {
    return null;
  }
  let t17;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <NavigationHeader leftButton={{
      variant: "close",
      confirmClose: true
    }} />;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const t18 = isSelfie ? undefined : docName;
  let t19;
  if ($[63] !== country || $[64] !== isSelfie || $[65] !== sideName || $[66] !== t18) {
    t19 = <DesktopHeader docName={t18} country={country} sideName={sideName} isSelfie={isSelfie} />;
    $[63] = country;
    $[64] = isSelfie;
    $[65] = sideName;
    $[66] = t18;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== showSlowConnectionMessage || $[69] !== step) {
    t20 = <Loading step={step} showSlowConnectionMessage={showSlowConnectionMessage} />;
    $[68] = showSlowConnectionMessage;
    $[69] = step;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Success />;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== mode || $[73] !== t20) {
    t22 = <div className="flex flex-col justify-center items-center bg-secondary border border-dashed border-primary rounded min-h-[280px]"><IdDocAnimation loadingComponent={t20} successComponent={t21} mode={mode} hasNextSide={false} /></div>;
    $[72] = mode;
    $[73] = t20;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  const t23 = mode === "loading";
  let t24;
  if ($[75] !== t) {
    t24 = t(_temp3);
    $[75] = t;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== handleNextStep || $[78] !== t23 || $[79] !== t24) {
    t25 = <Button fullWidth={true} disabled={t23} onClick={handleNextStep} size="large" data-dd-action-name="doc:continue">{t24}</Button>;
    $[77] = handleNextStep;
    $[78] = t23;
    $[79] = t24;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== t19 || $[82] !== t22 || $[83] !== t25) {
    t26 = <div className="flex flex-col gap-6 max-md:px-2">{t17}{t19}{t22}{t25}</div>;
    $[81] = t19;
    $[82] = t22;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  return t26;
};
export default DeskTopProcessing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}
function _temp2($) {
  return $["missing-requirement-error"];
}
function _temp3($_0) {
  return $_0.continue;
}